import * as React from 'react';

import { formatDate } from '@acceligentllc/shared/utils/time';

import type { ColorPalette } from '@acceligentllc/shared/enums/color';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import { Button } from '@acceligentllc/storybook';

import ColorSquare from 'af-components/ColorSquare';
import CustomModal from 'af-components/CustomModal';

import styles from './styles.module.scss';

interface BillingCodeSummary {
	customerId: string;
	unit: Nullable<string>;
	unitPrice: string;
	group: Nullable<string>;
	groupColor: Nullable<ColorPalette>;
	description: string;
	createdAt: Date;
}

interface OwnProps {
	showModal: boolean;
	billingCode: Nullable<BillingCodeSummary>;
	onClose: () => void;
	onConfirm: () => void;
}

const BillingCodeArchiveModal: React.FC<OwnProps> = (props) => {
	const {
		showModal,
		billingCode,
		onClose,
		onConfirm,
	} = props;

	const renderGroupOption = React.useCallback((option: Nullable<{
		name: string;
		color: Nullable<ColorPalette>;
	}>) => {
		if (!option) {
			return <div />;
		}

		return (
			<div className={styles['group-option']}>
				{option.color && <ColorSquare color={option.color} />}
				{option.name}
			</div>
		);
	}, []);

	const renderBillingCodeDescriptionItem = React.useCallback((item: React.ReactNode, index: number) => (
		<React.Fragment key={index}>
			{index > 0 && <span> • </span>}
			{item}
		</React.Fragment>
	), []);

	const billingCodeDescription = React.useMemo(() => {
		if (!billingCode) {
			return null;
		}

		const items: React.ReactNode[] = [
			billingCode.customerId,
			billingCode.unit,
			billingCode.unitPrice ? `$${billingCode.unitPrice}` : null,
			billingCode.group ? renderGroupOption({
				name: billingCode.group,
				color: billingCode.groupColor,
			}) : null,
			billingCode.description,
			<div key="active-since">
				Active since: {formatDate(billingCode.createdAt, TimeFormat.DATE_ONLY)}
			</div>,
		].filter(Boolean);

		if (!items.length) {
			return null;
		}

		return (
			<div className={styles['billing-code-description']}>
				{items.map(renderBillingCodeDescriptionItem)}
			</div>
		);
	}, [billingCode, renderBillingCodeDescriptionItem, renderGroupOption]);

	return (
		<CustomModal
			closeModal={onClose}
			modalStyle="warning"
			showModal={showModal}
			size="lg"
		>
			<CustomModal.Header
				closeModal={onClose}
				title="Archive billing code"
			/>

			<CustomModal.Body>
				<div className={styles['archive-modal-question']}>
					Are you sure you want to archive billing code?
				</div>

				{billingCode && (
					<div className={styles['modal-billing-code-container']}>
						<div className={styles.bold}>Billing Code:</div>
						<div>{billingCodeDescription}</div>
					</div>
				)}

				<div className={styles['archive-modal-warning-text']}>
					This item is linked to other data in the system. This action cannot be undone. Are you sure you want to continue?
				</div>
			</CustomModal.Body>

			<CustomModal.Footer>
				<Button
					label="Cancel"
					onClick={onClose}
					style="secondary"
				/>
				<Button
					label="Continue"
					onClick={onConfirm}
					style="primary"
				/>
			</CustomModal.Footer>
		</CustomModal>
	);
};

export default React.memo(BillingCodeArchiveModal);
